import * as XLSX from 'xlsx';
import { Dish } from '../types/dish';

/**
 * Parse Excel file and convert it to Dish objects
 * @param file The Excel file to parse
 * @returns Array of Dish objects
 */
export const parseExcelToDishes = (file: File): Promise<Dish[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        console.log('Excel data loaded:', !!data);
        
        const workbook = XLSX.read(data, { type: 'array' });
        console.log('Workbook sheets:', workbook.SheetNames);
        
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert Excel data to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        console.log('Parsed Excel data:', jsonData);
        
        // Map JSON data to Dish objects
        const dishes: Dish[] = jsonData.map((row: any) => {
          console.log('Processing row:', row);
          console.log('Row keys:', Object.keys(row));
          
          // Create a price configuration object based on Excel data
          const priceConfiguration = {
            allowPriceEdit: row['Cho phép sửa giá khi bán'] === 1 || row['Cho phép sửa giá khi bán'] === '1' || row['Cho phép sửa giá khi bán'] === true,
            requireQuantity: false, // Default value
            allowRemoveWithoutPermission: false // Default value
          };
          
          // Create the dish object with all available fields from Excel
          const dish: Dish = {
            name: row['Tên'] || '',
            price: Number(row['Giá']) || 0,
            code: row['Mã món'] || '',
            city: row['Thành phố'] || 'Hà Nội', // Default to Hà Nội if not specified
            unit: row['Đơn vị'] || 'Phần', // Default unit if not specified
            vat: Number(row['VAT (%)']) || 0,
            preparationTime: Number(row['Thời gian chế biến (phút)']) || 0,
            priceConfiguration: priceConfiguration,
            isActive: row['Trạng thái'] === 1 || row['Trạng thái'] === '1' || row['Trạng thái'] === true
          };
          
          // Add optional fields if they exist in the Excel file
          if (row['Mã barcode'] !== undefined) dish.barcode = String(row['Mã barcode']);
          if (row['Mô tả'] !== undefined) dish.description = String(row['Mô tả']);
          if (row['SKU'] !== undefined) dish.sku = String(row['SKU']);
          
          // Handle sideDish field
          if (row['Món ăn kèm'] !== undefined) {
            dish.sideDish = row['Món ăn kèm'] === 1 || row['Món ăn kèm'] === '1' || row['Món ăn kèm'] === true ? 'true' : '';
          }
          
          // Handle group field (Nhóm and Tên nhóm)
          if (row['Nhóm'] !== undefined || row['Tên nhóm'] !== undefined) {
            dish.group = String(row['Nhóm'] || row['Tên nhóm'] || '');
          }
          
          // Handle type field (Loại món and Tên loại)
          if (row['Loại món'] !== undefined || row['Tên loại'] !== undefined) {
            dish.type = String(row['Loại món'] || row['Tên loại'] || '');
          }
          
          // Handle image URL
          if (row['Hình ảnh'] !== undefined) dish.imageUrl = String(row['Hình ảnh']);
          
          // Handle virtual dish configuration
          if (row['Cấu hình món ảo'] !== undefined) {
            dish.isVirtualDish = row['Cấu hình món ảo'] === 1 || row['Cấu hình món ảo'] === '1' || row['Cấu hình món ảo'] === true;
          }
          
          // Handle buffet ticket configuration
          if (row['Cấu hình món ăn là vé buffet'] !== undefined) {
            dish.isBuffetTicket = row['Cấu hình món ăn là vé buffet'] === 1 || row['Cấu hình món ăn là vé buffet'] === '1' || row['Cấu hình món ăn là vé buffet'] === true;
          }
          
          // Handle QR code formula
          if (row['Công thức inQR cho máy pha trà'] !== undefined) {
            dish.qrCodeFormula = String(row['Công thức inQR cho máy pha trà']);
          }
          
          // Handle service configuration
          if (row['Cấu hình món dịch vụ'] !== undefined) {
            dish.serviceConfiguration = row['Cấu hình món dịch vụ'] === 1 || row['Cấu hình món dịch vụ'] === '1' || row['Cấu hình món dịch vụ'] === true;
          }
          
          // Handle display order
          if (row['Thứ tự'] !== undefined) {
            dish.displayOrder = Number(row['Thứ tự']) || 0;
          }
          
          // Handle side dish quantity not updated flag
          if (row['Không cập nhật số lượng món ăn kèm'] !== undefined) {
            dish.isSideDishQuantityNotUpdated = row['Không cập nhật số lượng món ăn kèm'] === 1 || row['Không cập nhật số lượng món ăn kèm'] === '1' || row['Không cập nhật số lượng món ăn kèm'] === true;
          }
          
          console.log('Created dish object:', dish);
          return dish;
        });
        
        console.log('Total dishes parsed:', dishes.length);
        resolve(dishes);
      } catch (error) {
        console.error('Error parsing Excel:', error);
        reject(error);
      }
    };
    
    reader.onerror = (error) => {
      console.error('FileReader error:', error);
      reject(error);
    };
    
    // Use readAsArrayBuffer instead of readAsBinaryString for better compatibility
    reader.readAsArrayBuffer(file);
  });
};

/**
 * Generate a sample Excel template for dish import
 * @returns Workbook object that can be used to download a template
 */
export const generateDishExcelTemplate = (): XLSX.WorkBook => {
  // Define the columns for the template with Vietnamese column names
  const columns = [
    'ID', 'Mã món', 'Thành phố', 'Tên', 'Giá', 'Trạng thái', 'Mã barcode', 
    'Món ăn kèm', 'Không cập nhật số lượng món ăn kèm', 'Đơn vị', 'Nhóm', 
    'Tên nhóm', 'Loại món', 'Tên loại', 'Mô tả', 'SKU', 'VAT (%)', 
    'Thời gian chế biến (phút)', 'Cho phép sửa giá khi bán', 'Cấu hình món ảo', 
    'Cấu hình món dịch vụ', 'Cấu hình món ăn là vé buffet', 'Giờ', 'Ngày', 
    'Thứ tự', 'Hình ảnh', 'Công thức inQR cho máy pha trà'
  ];
  
  // Create a worksheet with the columns
  const ws = XLSX.utils.aoa_to_sheet([columns]);
  
  // Create a workbook with the worksheet
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Dishes');
  
  return wb;
};

/**
 * Download Excel template for dish import
 */
export const downloadDishExcelTemplate = (): void => {
  const wb = generateDishExcelTemplate();
  XLSX.writeFile(wb, 'mau-cap-nhat-mon-an.xlsx');
};
